/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-refresh/only-export-components */
import { useShortCode } from '@/hooks/use-local-storage.js'
import { useAuthRefreshMutation } from '@/services/authApi.js'
import { Session } from '@supabase/supabase-js'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react'
import supabase from '../supabaseClient.js'

interface SessionContextType {
  session: Session | null
  loading: boolean
}

const SessionContext = createContext<SessionContextType | undefined>(undefined)

interface SessionProviderProps {
  children: ReactNode
}

export const SessionProvider = ({ children }: SessionProviderProps) => {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [authRefresh] = useAuthRefreshMutation()
  const isRefreshingRef = useRef(false)
  const refreshPromiseRef = useRef<Promise<any> | null>(null)

  const venue_short_code = useShortCode()

  const handleLogout = async () => {
    try {
      // Step 1: OneSignal cleanup
      try {
        const OneSignal = (window as any).OneSignal;
        if (OneSignal && OneSignal.logout) {
          await OneSignal.logout();
        }
      } catch (error) {
      }
  
      // Step 2: Clear service workers (MISSING PIECE!)
      try {
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            if (registration.scope.includes('onesignal')) {
              await registration.unregister();
            }
          }
        }
      } catch (error) {
      }
  
      // Step 3: Reset OneSignal service (MISSING PIECE!)
      try {
        const { OneSignalService } = await import('@/services/oneSignalService');
        OneSignalService.reset();
      } catch (error) {
      }
  
      // Step 4: Preserve device-specific settings BEFORE clearing everything
      const deviceSpecificData = {
        pwa_dismissed: localStorage.getItem('pwa_install_prompt_dismissed'),
        pwa_timestamp: localStorage.getItem('pwa_dismissal_timestamp'),
        is_ios_pwa: localStorage.getItem('is_ios_pwa'),
      };
  
      // Step 5: Supabase logout
      await supabase.auth.signOut();
      
      // Step 5.1: Clear ALL OneSignal-related data for complete isolation
      localStorage.removeItem('oneSignalId');           // ✅ Safe to clear
      localStorage.removeItem('externalUserId');        // User mapping
      localStorage.removeItem('notifications_registered'); // User preference
      localStorage.removeItem('notifications_status');     // User preference
  
      // Step 6: Clear ALL storage (this clears OneSignal cache too)
      localStorage.clear();
      sessionStorage.clear();
  
      // Step 7: Restore device-specific settings that should persist across logins
      if (deviceSpecificData.pwa_dismissed) {
        localStorage.setItem('pwa_install_prompt_dismissed', deviceSpecificData.pwa_dismissed);
      }
      if (deviceSpecificData.pwa_timestamp) {
        localStorage.setItem('pwa_dismissal_timestamp', deviceSpecificData.pwa_timestamp);
      }
      if (deviceSpecificData.is_ios_pwa) {
        localStorage.setItem('is_ios_pwa', deviceSpecificData.is_ios_pwa);
      }
      
      // Step 8: Reset session state
      setSession(null);
      
    } catch (error) {
      console.error('Error logging out:', error);
      
      // Fallback cleanup with device preservation
      const deviceData = {
        pwa_dismissed: localStorage.getItem('pwa_install_prompt_dismissed'),
        pwa_timestamp: localStorage.getItem('pwa_dismissal_timestamp'),
        is_ios_pwa: localStorage.getItem('is_ios_pwa')
      };
      
      localStorage.clear();
      sessionStorage.clear();
      
      // Restore device-specific data
      if (deviceData.pwa_dismissed) {
        localStorage.setItem('pwa_install_prompt_dismissed', deviceData.pwa_dismissed);
      }
      if (deviceData.pwa_timestamp) {
        localStorage.setItem('pwa_dismissal_timestamp', deviceData.pwa_timestamp);
      }
      if (deviceData.is_ios_pwa) {
        localStorage.setItem('is_ios_pwa', deviceData.is_ios_pwa);
      }
      
      setSession(null);
      window.location.reload();
    }
  }

  const refreshToken = async () => {
    // If already refreshing, return the existing promise
    if (isRefreshingRef.current && refreshPromiseRef.current) {
      return refreshPromiseRef.current
    }

    isRefreshingRef.current = true
    
    refreshPromiseRef.current = (async () => {
      try {
        
        const res = await authRefresh({ venue_short_code }).unwrap()
        const newExpiresAt = Math.floor(Date.now() / 1000) + (res.expires_in || 3600)

        // Update localStorage immediately
        localStorage.setItem('refreshToken', res.refresh_token)
        localStorage.setItem('adminToken', res.access_token ?? res.token)
        localStorage.setItem('expires_at', String(newExpiresAt))

        return res
      } catch (error) {
        console.error('Token refresh failed:', error)
        throw error
      } finally {
        isRefreshingRef.current = false
        refreshPromiseRef.current = null
      }
    })()

    return refreshPromiseRef.current
  }

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const adminToken = localStorage.getItem('adminToken')
      const expires_at = localStorage.getItem('expires_at')
      
      if (!adminToken || !expires_at) {
        setLoading(false)
        return
      }
      
      const currentTime = Math.floor(Date.now() / 1000)
      const expiresAt = Number(expires_at)
      
      // If token is expired or expires in 5 minutes, refresh it
      if (currentTime >= expiresAt - 300) {
        try {
          const res = await refreshToken()
          const newExpiresAt = Math.floor(Date.now() / 1000) + (res.expires_in || 3600)
          
          setSession({ 
            access_token: res.access_token,
            refresh_token: res.refresh_token,
            expires_at: newExpiresAt 
          } as Session)
        } catch (error) {
          handleLogout()
        }
      } else {
        setSession({ access_token: adminToken } as Session)
      }
      
      setLoading(false)
    }
    
    checkAndRefreshToken()

    // Set up Supabase auth listener (simplified)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, supabaseSession) => {

      if (event === 'SIGNED_OUT' || !supabaseSession) {
        setSession(null)
        return
      }

      // Only handle token refresh on auth state changes if not already refreshing
      if (event === 'TOKEN_REFRESHED' && !isRefreshingRef.current) {
        const expires_at = localStorage.getItem('expires_at')
        const expiresAt = expires_at ? Number(expires_at) : 0
        
        if (expiresAt > 0) {
          setSession({
            ...supabaseSession,
            expires_at: expiresAt,
          })
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [authRefresh, venue_short_code])

  return (
    <SessionContext.Provider value={{ session, loading }}>
      {children}
    </SessionContext.Provider>
  )
}

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext)
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider')
  }
  return context
}